import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import type { OperationsReport, OperationsWorkload } from "./api";

export type OperationsReportExportPeriod = Pick<OperationsReport["filters"], "from" | "to">;
type CellValue = string | number;
type ExportTable = { title: string; headers: string[]; rows: CellValue[][] };

const numberFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });

/** Formatiert ISO-Kalenderdaten ohne zeitzonenbedingte Verschiebung. */
export function formatGermanDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  const candidate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (candidate.getUTCFullYear() !== Number(year) || candidate.getUTCMonth() !== Number(month) - 1 || candidate.getUTCDate() !== Number(day)) return value;
  return `${day}.${month}.${year}`;
}

function formatNumber(value: number): string {
  return numberFormat.format(value);
}

function periodLabel(period: OperationsReportExportPeriod): string {
  return `${formatGermanDate(period.from)} bis ${formatGermanDate(period.to)}`;
}

function filenameDate(period: OperationsReportExportPeriod): string {
  return `${period.from}_${period.to}`.replace(/[^0-9_]/g, "");
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function workloadRows(items: OperationsWorkload[]): CellValue[][] {
  return items.map((item) => [
    item.name,
    item.location?.name ?? "—",
    item.sessions,
    item.bookedHours,
    item.bookedDays,
    item.bottleneck ? "Ja" : "Nein",
    item.bottleneckReason ?? "—",
  ]);
}

export function getOperationsReportExportTables(report: OperationsReport): ExportTable[] {
  return [
    {
      title: "Kennzahlen",
      headers: ["Kennzahl", "Wert"],
      rows: [
        ["Sessions", report.summary.sessions],
        ["Teilnehmende", report.summary.participants],
        ["Teilnehmendenstunden", report.summary.participantHours],
        ["Stornierte Sessions", report.summary.cancelledSessions],
        ["Stornoquote (%)", report.summary.cancellationRatePercent],
      ],
    },
    {
      title: "Raumbelegung",
      headers: ["Standort", "Raum", "Kapazität", "Sessions", "Belegte Stunden", "Teilnehmende", "Freie Plätze", "Sitzplatzauslastung (%)"],
      rows: report.roomUsage.map((row) => [row.location.name, row.roomName, row.capacity, row.sessions, row.occupiedHours, row.participants, row.freeSeats, row.seatUtilizationPercent]),
    },
    {
      title: "Teilnehmerentwicklung nach Kurs",
      headers: ["Kurs", "Sessions", "Teilnehmende", "Teilnehmendenstunden"],
      rows: report.participantsByCourse.map((row) => [row.label, row.sessions, row.participants, row.participantHours]),
    },
    {
      title: "Teilnehmerentwicklung nach Standort",
      headers: ["Standort", "Sessions", "Teilnehmende", "Teilnehmendenstunden"],
      rows: report.participantsByLocation.map((row) => [row.location.name, row.sessions, row.participants, row.participantHours]),
    },
    {
      title: "Teilnehmerentwicklung nach Trainer",
      headers: ["Trainer", "Sessions", "Teilnehmende", "Teilnehmendenstunden"],
      rows: report.participantsByTrainer.map((row) => [row.trainerName, row.sessions, row.participants, row.participantHours]),
    },
    {
      title: "Trainer-Arbeitslast",
      headers: ["Name", "Standort", "Sessions", "Gebuchte Stunden", "Gebuchte Tage", "Engpass", "Engpassgrund"],
      rows: workloadRows(report.trainerWorkload),
    },
    {
      title: "Ressourcen-Arbeitslast",
      headers: ["Name", "Standort", "Sessions", "Gebuchte Stunden", "Gebuchte Tage", "Engpass", "Engpassgrund"],
      rows: workloadRows(report.resourceWorkload),
    },
    {
      title: "Status und Storno im Zeitverlauf",
      headers: ["Zeitraum", "Status", "Sessions", "Stornierte Sessions", "Stornoquote (%)"],
      rows: report.statusTrend.map((row) => [row.period, row.status, row.sessions, row.cancelledSessions, row.cancellationRatePercent]),
    },
  ];
}

function csvEscape(value: CellValue): string {
  const text = String(value);
  return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Exportiert den aktuell geladenen OperationsReport als UTF-8-BOM-CSV mit Semikolon-Trennzeichen. */
export function exportOperationsReportCsv(report: OperationsReport, period: OperationsReportExportPeriod = report.filters): void {
  const lines = [
    ["Operative Auswertung"],
    ["Zeitraum", periodLabel(period)],
    [],
    ...getOperationsReportExportTables(report).flatMap((table) => [[table.title], table.headers, ...table.rows, []]),
  ];
  const csv = `\uFEFF${lines.map((row) => row.map(csvEscape).join(";")).join("\r\n")}`;
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `operative-auswertung_${filenameDate(period)}.csv`);
}

/** Exportiert Kennzahlen, Raumbelegung und Arbeitslasten als Excel-Arbeitsmappe. */
export function exportOperationsReportXlsx(report: OperationsReport, period: OperationsReportExportPeriod = report.filters): void {
  const workbook = XLSX.utils.book_new();
  for (const table of getOperationsReportExportTables(report)) {
    const sheet = XLSX.utils.aoa_to_sheet([
      ["Operative Auswertung"],
      ["Zeitraum", periodLabel(period)],
      [],
      table.headers,
      ...table.rows,
    ]);
    sheet["!cols"] = table.headers.map((header, index) => ({ wch: Math.max(header.length + 2, ...table.rows.map((row) => String(row[index] ?? "").length + 2), 12) }));
    XLSX.utils.book_append_sheet(workbook, sheet, table.title);
  }
  XLSX.writeFile(workbook, `operative-auswertung_${filenameDate(period)}.xlsx`, { compression: true });
}

/** Exportiert eine kompakte, druckfreundliche PDF-Kurzfassung mit Kennzahlen und zentralen Tabellen. */
export function exportOperationsReportPdf(report: OperationsReport, period: OperationsReportExportPeriod = report.filters): void {
  const document = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const tables = getOperationsReportExportTables(report);
  const metrics = tables[0];
  if (!metrics) return;
  const margin = 14;
  document.setFont("helvetica", "bold");
  document.setFontSize(16);
  document.text("Operative Auswertung", margin, 16);
  document.setFont("helvetica", "normal");
  document.setFontSize(10);
  document.text(`Zeitraum: ${periodLabel(period)}`, margin, 23);

  autoTable(document, {
    startY: 28,
    head: [metrics.headers],
    body: metrics.rows.map((row) => {
      const label = row[0] ?? "";
      const value = row[1] ?? "";
      return [label, typeof value === "number" ? formatNumber(value) : value];
    }),
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [30, 64, 175] },
    margin: { left: margin, right: margin },
    tableWidth: 115,
  });

  for (const table of tables.slice(1)) {
    const previous = (document as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable;
    const startY = (previous?.finalY ?? 28) + 10;
    if (startY > 175) document.addPage();
    const y = startY > 175 ? 18 : startY;
    document.setFont("helvetica", "bold");
    document.setFontSize(12);
    document.text(table.title, margin, y);
    autoTable(document, {
      startY: y + 4,
      head: [table.headers],
      body: table.rows.map((row) => row.map((value) => typeof value === "number" ? formatNumber(value) : value)),
      theme: "grid",
      styles: { fontSize: 7.5, cellPadding: 1.8, overflow: "linebreak" },
      headStyles: { fillColor: [30, 64, 175], fontSize: 7.5 },
      margin: { left: margin, right: margin },
      didDrawPage: () => {
        document.setFont("helvetica", "normal");
        document.setFontSize(8);
        document.text(`Operative Auswertung · ${periodLabel(period)}`, margin, document.internal.pageSize.getHeight() - 7);
      },
    });
  }
  document.save(`operative-auswertung_${filenameDate(period)}.pdf`);
}
